/**
 * Lightbox.jsx — an image, as large as the screen allows.
 *
 * Opened from a post's photos and an article's cover. Click the picture to see
 * it at full resolution and scroll around it; click outside, press Escape or
 * use the close button to leave. Arrow keys and the side buttons step through
 * a post's images.
 *
 * Rendered into document.body so no card's stacking context or overflow can
 * clip it, with the page behind it locked from scrolling and focus returned to
 * whatever opened it.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Lightbox({ images = [], index = 0, onClose }) {
  const list = images.filter((m) => m && m.url);
  const count = list.length;
  const [i, setI] = useState(Math.min(Math.max(index, 0), Math.max(count - 1, 0)));
  const [zoomed, setZoomed] = useState(false);
  const closeRef = useRef(null);

  const step = useCallback((d) => {
    setZoomed(false);
    setI((v) => (v + d + count) % count);
  }, [count]);

  useEffect(() => {
    const opener = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowRight' && count > 1) step(1);
      else if (e.key === 'ArrowLeft' && count > 1) step(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, [count, onClose, step]);

  if (!count) return null;
  const item = list[i];

  return createPortal(
    <div
      className={`lightbox${zoomed ? ' is-zoomed' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={item.alt ? `Image: ${item.alt}` : 'Image'}
      onClick={onClose}
    >
      <button
        ref={closeRef}
        type="button"
        className="lightbox__btn lightbox__close"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close image"
      >
        <X size={22} aria-hidden="true" />
      </button>

      {count > 1 && (
        <button
          type="button"
          className="lightbox__btn lightbox__nav lightbox__nav--prev"
          onClick={(e) => { e.stopPropagation(); step(-1); }}
          aria-label="Previous image"
        >
          <ChevronLeft size={26} aria-hidden="true" />
        </button>
      )}

      <div className="lightbox__stage" onClick={(e) => e.stopPropagation()}>
        <img
          key={item.url}
          className="lightbox__img"
          src={item.url}
          alt={item.alt || ''}
          onClick={() => setZoomed((z) => !z)}
          title={zoomed ? 'Click to fit the screen' : 'Click to see full size'}
        />
      </div>

      {(item.alt || count > 1) && (
        <p className="lightbox__caption" onClick={(e) => e.stopPropagation()}>
          {item.alt}
          {count > 1 && <span className="lightbox__count">{i + 1} of {count}</span>}
        </p>
      )}

      {count > 1 && (
        <button
          type="button"
          className="lightbox__btn lightbox__nav lightbox__nav--next"
          onClick={(e) => { e.stopPropagation(); step(1); }}
          aria-label="Next image"
        >
          <ChevronRight size={26} aria-hidden="true" />
        </button>
      )}
    </div>,
    document.body
  );
}
